/**
 * Advanced AI Strategy
 *
 * Master-level AI that combines multiple techniques:
 * - Opening book for theoretical play
 * - Minimax with alpha-beta pruning for midgame
 * - Endgame solver for perfect endplay
 * - Adaptive time management
 * - Monte Carlo Tree Search for complex positions
 */

import { AIDifficulty, PlayerColor, BoardPosition } from '@/types/game-types';
import type { GameState } from '@/core/models/GameState';
import { AIStrategyBase, type AIMoveResult } from '../ai-strategy-base';
import { MinimaxAI, type MinimaxConfig } from './minimax-ai';
import { OpeningBook, defaultOpeningBook } from './opening-book';
import { EndgameSolver, defaultEndgameSolver } from './endgame-solver';
import { GameSimulator, type SimulatedGameState } from '../game-simulator';
import { AdvancedEvaluator } from './evaluation';

/**
 * Advanced AI configuration
 */
export interface AdvancedAIConfig {
  // Opening book settings
  useOpeningBook: boolean;
  openingBookDepth: number;

  // Midgame minimax settings
  minimaxDepth: number;
  minimaxTimeLimit: number;
  useIterativeDeepening: boolean;

  // Endgame solver settings
  useEndgameSolver: boolean;
  endgameThreshold: number; // Empty squares to trigger endgame
  endgameTimeLimit: number;

  // Adaptive settings
  adaptiveTimeManagement: boolean;
  thinkingTimeBase: number; // Base thinking time in ms
  thinkingTimeMax: number;  // Maximum thinking time in ms

  // Advanced features
  useMonteCarlo: boolean;
  monteCarloSamples: number;
  useAspirationWindows: boolean;
}

/**
 * Game phase detection
 */
type GamePhase = 'opening' | 'midgame' | 'endgame';

/**
 * Advanced AI Strategy Implementation
 */
export class AdvancedAI extends AIStrategyBase {
  public readonly difficulty = AIDifficulty.ADVANCED;
  public readonly name = 'Advanced AI';
  public readonly description = 'Master-level AI combining opening book, minimax, and endgame solver';

  private readonly _config: AdvancedAIConfig;
  private readonly _openingBook: OpeningBook;
  private readonly _endgameSolver: EndgameSolver;
  private _minimaxAI: MinimaxAI;

  // Performance tracking
  private _phaseStats = {
    opening: { moves: 0, totalTime: 0 },
    midgame: { moves: 0, totalTime: 0 },
    endgame: { moves: 0, totalTime: 0 },
  };

  constructor(config: Partial<AdvancedAIConfig> = {}) {
    super();

    this._config = {
      // Opening book
      useOpeningBook: true,
      openingBookDepth: 12,

      // Midgame minimax
      minimaxDepth: 8,
      minimaxTimeLimit: 5000,
      useIterativeDeepening: true,

      // Endgame solver
      useEndgameSolver: true,
      endgameThreshold: 16,
      endgameTimeLimit: 30000,

      // Adaptive time management
      adaptiveTimeManagement: true,
      thinkingTimeBase: 1000,
      thinkingTimeMax: 10000,

      // Advanced features
      useMonteCarlo: false, // Disabled for now
      monteCarloSamples: 1000,
      useAspirationWindows: true,

      ...config,
    };

    this._openingBook = defaultOpeningBook;
    this._endgameSolver = defaultEndgameSolver;

    // Configure minimax AI
    const minimaxConfig: Partial<MinimaxConfig> = {
      maxDepth: this._config.minimaxDepth,
      maxTime: this._config.minimaxTimeLimit,
      useIterativeDeepening: this._config.useIterativeDeepening,
      useTranspositionTable: true,
      useMoveOrdering: true,
      aspirationWindow: this._config.useAspirationWindows ? 50 : undefined,
    };

    this._minimaxAI = new MinimaxAI(minimaxConfig);
  }

  protected async selectMoveImpl(
    gameState: GameState,
    player: PlayerColor,
    timeLimit: number,
  ): Promise<AIMoveResult> {
    const startTime = Date.now();
    const phase = this.detectGamePhase(gameState);
    const adaptiveTimeLimit = this.calculateAdaptiveTimeLimit(gameState, timeLimit, phase);

    let result: AIMoveResult;

    try {
      switch (phase) {
        case 'opening':
          result = await this.handleOpening(gameState, player, adaptiveTimeLimit);
          break;

        case 'midgame':
          result = await this.handleMidgame(gameState, player, adaptiveTimeLimit);
          break;

        case 'endgame':
          result = await this.handleEndgame(gameState, player, adaptiveTimeLimit);
          break;

        default:
          result = await this.handleMidgame(gameState, player, adaptiveTimeLimit);
      }

      // Update performance statistics
      const actualTime = Date.now() - startTime;
      this._phaseStats[phase].moves++;
      this._phaseStats[phase].totalTime += actualTime;

      // Enhance result with phase information
      return {
        ...result,
        thinkingTime: actualTime,
      };

    } catch (error) {
      // Fallback to minimax if any component fails
      console.warn(`Advanced AI ${phase} phase failed, falling back to minimax:`, error);
      return this._minimaxAI.selectMove(gameState, player, adaptiveTimeLimit);
    }
  }

  /**
   * Handle opening phase using opening book
   */
  private async handleOpening(
    gameState: GameState,
    player: PlayerColor,
    timeLimit: number,
  ): Promise<AIMoveResult> {
    if (this._config.useOpeningBook && this._openingBook.hasOpeningMove(gameState)) {
      const openingMove = this._openingBook.getOpeningMove(gameState);

      if (openingMove !== null) {
        // Simulate thinking time for opening book moves
        const thinkingTime = 200 + Math.random() * 300; // 200-500ms
        await new Promise(resolve => setTimeout(resolve, thinkingTime));

        return {
          position: openingMove,
          confidence: 0.9, // High confidence in opening theory
          evaluation: 0, // Opening moves are theoretical
          thinkingTime,
          depth: this._openingBook.getOpeningDepth(gameState),
          nodesEvaluated: 1,
        };
      }
    }

    // Fall back to minimax if no opening book move
    return this._minimaxAI.selectMove(gameState, player, timeLimit);
  }

  /**
   * Handle midgame using minimax with advanced evaluation
   */
  private async handleMidgame(
    gameState: GameState,
    player: PlayerColor,
    timeLimit: number,
  ): Promise<AIMoveResult> {
    // Use the full minimax AI for midgame
    return this._minimaxAI.selectMove(gameState, player, timeLimit);
  }

  /**
   * Handle endgame using exact solver
   */
  private async handleEndgame(
    gameState: GameState,
    player: PlayerColor,
    timeLimit: number,
  ): Promise<AIMoveResult> {
    if (!this._config.useEndgameSolver) {
      return this._minimaxAI.selectMove(gameState, player, timeLimit);
    }

    const simState = GameSimulator.fromGameState(gameState);

    if (this._endgameSolver.canSolve(simState)) {
      try {
        const endgameResult = await this._endgameSolver.solvePosition(
          simState,
          player,
          Math.min(timeLimit, this._config.endgameTimeLimit),
        );

        return {
          position: endgameResult.bestMove,
          confidence: endgameResult.confidence,
          evaluation: endgameResult.exactScore,
          thinkingTime: endgameResult.timeElapsed,
          depth: endgameResult.movesToEnd,
          nodesEvaluated: endgameResult.nodesSearched,
        };
      } catch (error) {
        console.warn('Endgame solver failed, falling back to minimax:', error);
      }
    }

    // Fall back to deep minimax search
    const deepMinimaxConfig = {
      maxDepth: this._config.minimaxDepth + 2, // Extra depth for endgame
      maxTime: timeLimit,
      useIterativeDeepening: true,
    };

    const deepMinimax = new MinimaxAI(deepMinimaxConfig);
    return deepMinimax.selectMove(gameState, player, timeLimit);
  }

  /**
   * Detect current game phase
   */
  private detectGamePhase(gameState: GameState): GamePhase {
    const totalPieces = gameState.scores.black + gameState.scores.white;
    const emptySquares = 64 - totalPieces;

    // Opening phase: first 8-12 moves
    if (totalPieces <= 12) {
      return 'opening';
    }

    // Endgame phase: 16 or fewer empty squares
    if (emptySquares <= this._config.endgameThreshold) {
      return 'endgame';
    }

    // Everything else is midgame
    return 'midgame';
  }

  /**
   * Calculate adaptive thinking time based on position complexity
   */
  private calculateAdaptiveTimeLimit(
    gameState: GameState,
    baseTimeLimit: number,
    phase: GamePhase,
  ): number {
    if (!this._config.adaptiveTimeManagement) {
      return baseTimeLimit;
    }

    const moveCount = gameState.validMoves.size;
    const complexity = this.assessPositionComplexity(gameState);

    let timeMultiplier = 1.0;

    // Phase-based adjustments
    switch (phase) {
      case 'opening':
        timeMultiplier = 0.3; // Quick opening moves
        break;
      case 'midgame':
        timeMultiplier = 1.0; // Standard time
        break;
      case 'endgame':
        timeMultiplier = 2.0; // More time for critical endgame
        break;
    }

    // Complexity adjustments
    timeMultiplier *= (1.0 + complexity * 0.5);

    // Move count adjustments (more time when many options)
    if (moveCount > 10) {
      timeMultiplier *= 1.3;
    } else if (moveCount < 3) {
      timeMultiplier *= 0.7;
    }

    const adaptiveTime = Math.min(
      this._config.thinkingTimeMax,
      Math.max(
        this._config.thinkingTimeBase * timeMultiplier,
        baseTimeLimit * 0.5, // Don't go below 50% of base time
      ),
    );

    return Math.min(adaptiveTime, baseTimeLimit);
  }

  /**
   * Assess position complexity for time allocation
   */
  private assessPositionComplexity(gameState: GameState): number {
    let complexity = 0;

    // More moves = higher complexity
    complexity += Math.min(gameState.validMoves.size / 15, 1.0);

    // Score difference affects complexity
    const scoreDiff = Math.abs(gameState.scores.black - gameState.scores.white);
    complexity += Math.min(scoreDiff / 20, 0.5);

    // Corner and edge availability
    const corners = [0, 7, 56, 63];
    const availableCorners = corners.filter(pos => gameState.board[pos] === PlayerColor.EMPTY);
    complexity += availableCorners.length * 0.1;

    return Math.min(complexity, 1.0);
  }

  /**
   * Get current configuration
   */
  getConfig(): AdvancedAIConfig {
    return { ...this._config };
  }

  /**
   * Update configuration
   */
  setConfig(config: Partial<AdvancedAIConfig>): void {
    Object.assign(this._config, config);

    // Update minimax configuration
    const minimaxConfig: Partial<MinimaxConfig> = {
      maxDepth: this._config.minimaxDepth,
      maxTime: this._config.minimaxTimeLimit,
      useIterativeDeepening: this._config.useIterativeDeepening,
      aspirationWindow: this._config.useAspirationWindows ? 50 : undefined,
    };

    this._minimaxAI.setConfig(minimaxConfig);
  }

  /**
   * Get performance statistics
   */
  getPerformanceStats(): {
    opening: { moves: number; avgTime: number };
    midgame: { moves: number; avgTime: number };
    endgame: { moves: number; avgTime: number };
    totalMoves: number;
    overallAvgTime: number;
  } {
    const opening = {
      moves: this._phaseStats.opening.moves,
      avgTime: this._phaseStats.opening.moves > 0
        ? this._phaseStats.opening.totalTime / this._phaseStats.opening.moves
        : 0,
    };

    const midgame = {
      moves: this._phaseStats.midgame.moves,
      avgTime: this._phaseStats.midgame.moves > 0
        ? this._phaseStats.midgame.totalTime / this._phaseStats.midgame.moves
        : 0,
    };

    const endgame = {
      moves: this._phaseStats.endgame.moves,
      avgTime: this._phaseStats.endgame.moves > 0
        ? this._phaseStats.endgame.totalTime / this._phaseStats.endgame.moves
        : 0,
    };

    const totalMoves = opening.moves + midgame.moves + endgame.moves;
    const totalTime = this._phaseStats.opening.totalTime +
                      this._phaseStats.midgame.totalTime +
                      this._phaseStats.endgame.totalTime;

    return {
      opening,
      midgame,
      endgame,
      totalMoves,
      overallAvgTime: totalMoves > 0 ? totalTime / totalMoves : 0,
    };
  }

  /**
   * Reset performance statistics
   */
  resetStats(): void {
    this._phaseStats = {
      opening: { moves: 0, totalTime: 0 },
      midgame: { moves: 0, totalTime: 0 },
      endgame: { moves: 0, totalTime: 0 },
    };
  }

  /**
   * Clear all caches
   */
  clearCaches(): void {
    this._minimaxAI.clearCaches();
    this._endgameSolver.clearCaches();
  }
}

/**
 * Factory functions for creating advanced AI variants
 */
export const createAdvancedAI = {
  /**
   * Tournament-level AI with balanced settings
   */
  tournament(): AdvancedAI {
    const isMobile = this.isMobileDevice();

    if (isMobile) {
      console.log('📱 Creating mobile-optimized Tournament AI');
      return new AdvancedAI({
        useOpeningBook: true,
        openingBookDepth: 10,
        minimaxDepth: 6,
        useEndgameSolver: false, // Disable for mobile
        endgameThreshold: 14,
        adaptiveTimeManagement: true,
        thinkingTimeBase: 1500,
        thinkingTimeMax: 6000,
      });
    }

    return new AdvancedAI({
      useOpeningBook: true,
      openingBookDepth: 12,
      minimaxDepth: 8,
      useEndgameSolver: true,
      endgameThreshold: 16,
      adaptiveTimeManagement: true,
      thinkingTimeBase: 2000,
      thinkingTimeMax: 15000,
    });
  },

  /**
   * Master-level AI with maximum strength
   */
  master(): AdvancedAI {
    // Detect if running on mobile device
    const isMobile = this.isMobileDevice();

    if (isMobile) {
      console.log('📱 Creating mobile-optimized Master AI');
      return new AdvancedAI({
        useOpeningBook: true,
        openingBookDepth: 12,
        minimaxDepth: 6,
        useEndgameSolver: false, // Disable for mobile performance
        endgameThreshold: 16,
        adaptiveTimeManagement: true,
        thinkingTimeBase: 2000,
        thinkingTimeMax: 8000,
        useAspirationWindows: false, // Disable for mobile
      });
    }

    return new AdvancedAI({
      useOpeningBook: true,
      openingBookDepth: 16,
      minimaxDepth: 10,
      useEndgameSolver: true,
      endgameThreshold: 20,
      adaptiveTimeManagement: true,
      thinkingTimeBase: 3000,
      thinkingTimeMax: 30000,
      useAspirationWindows: true,
    });
  },

  /**
   * Mobile device detection utility
   */
  isMobileDevice(): boolean {
    const userAgent = navigator.userAgent.toLowerCase();
    const mobileKeywords = ['mobile', 'android', 'iphone', 'ipad', 'ipod', 'blackberry', 'windows phone'];
    const isMobileUA = mobileKeywords.some(keyword => userAgent.includes(keyword));
    const isSmallScreen = window.innerWidth < 768;
    const hasTouchSupport = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    return isMobileUA || (isSmallScreen && hasTouchSupport);
  },

  /**
   * Fast AI for quick games
   */
  blitz(): AdvancedAI {
    return new AdvancedAI({
      useOpeningBook: true,
      openingBookDepth: 8,
      minimaxDepth: 6,
      useEndgameSolver: true,
      endgameThreshold: 12,
      adaptiveTimeManagement: false,
      thinkingTimeBase: 500,
      thinkingTimeMax: 2000,
    });
  },

  /**
   * Custom advanced AI
   */
  custom(config: Partial<AdvancedAIConfig>): AdvancedAI {
    return new AdvancedAI(config);
  },
};